import React, { useState, useEffect } from "react";
import axios from "../../api/api";
import { Trash, Pencil } from "lucide-react";
import { toast } from "react-toastify";

export default function AddCountry() {
  const [countries, setCountries] = useState([]);
  const [newCountry, setNewCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  useEffect(() => {
    fetchCountries();
  }, []);

  // Fetch Countries
  const fetchCountries = async () => {
    setLoading(true);
    try {
      const resp = await axios.get("/api/country");
      setCountries(resp.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load countries");
    } finally {
      setLoading(false);
    }
  };

  // Add Country
  const handleAddCountry = async (e) => {
    e.preventDefault();

    const name = newCountry.trim();
    if (!name) {
      toast.error("Country name cannot be empty");
      return;
    }

    setLoading(true);
    try {
      await axios.post("/api/country/add-country", { name });
      toast.success("Country added successfully");
      setNewCountry("");
      fetchCountries();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add country");
    } finally {
      setLoading(false);
    }
  };

  // Delete Country
  const handleDeleteCountry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this country?")) {
      return;
    }

    setDeletingId(id);
    try {
      await axios.delete(`/api/country/${id}`);
      toast.success("Country deleted successfully");
      setCountries((prev) => prev.filter((c) => c._id !== id));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete country");
    } finally {
      setDeletingId(null);
    }
  };

  // Start Edit
  const startEdit = (country) => {
    setEditingId(country._id);
    setEditingName(country.name);
  };

  // Cancel Edit
  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  // Update Country
  const handleUpdateCountry = async (id) => {
    const name = editingName.trim();
    if (!name) {
      toast.error("Country name cannot be empty");
      return;
    }

    try {
      await axios.put(`/api/country/${id}`, { name });
      toast.success("Country updated successfully");

      setCountries((prev) =>
        prev.map((c) => (c._id === id ? { ...c, name } : c))
      );

      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update country");
    }
  };

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24">
        <div className="xl:w-full min-h-[calc(100vh-152px)] pb-14">
          <div className="grid xl:grid-cols-12 gap-6">
            <div className="xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">

                {/* Header */}
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 flex justify-between items-center">
                  <h4 className="font-semibold text-xl text-gray-800 dark:text-gray-200">
                    Manage Countries
                  </h4>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Total: {countries.length}
                  </span>
                </div>

                {/* Body */}
                <form onSubmit={handleAddCountry} className="p-6">

                  {/* Add Country */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Add New Country
                    </label>
                    <input
                      type="text"
                      value={newCountry}
                      onChange={(e) => setNewCountry(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-gray-200"
                      placeholder="Enter country name"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
                  >
                    {loading ? "Adding..." : "Add Country"}
                  </button>

                  {/* Country List */}
                  <div className="mt-8">
                    <h5 className="font-semibold text-lg text-gray-800 dark:text-gray-200 mb-3">
                      Existing Countries
                    </h5>

                    {loading ? (
                      <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                          />
                        ))}
                      </div>
                    ) : countries.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No countries found
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {countries.map((country) => (
                          <div
                            key={country._id}
                            className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:shadow transition"
                          >
                            {editingId === country._id ? (
                              <input
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                className="px-2 py-1 border rounded-md w-full mr-3 border-0 outline-none focus:outline-none focus:ring-0"
                              />
                            ) : (
                              <span className="font-medium text-gray-800 dark:text-gray-200">
                                {country.name}
                              </span>
                            )}

                            <div className="flex items-center gap-2">
                              {editingId === country._id ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateCountry(country._id)}
                                    className="text-green-600 text-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelEdit}
                                    className="text-gray-500 text-sm"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => startEdit(country)}
                                    className="p-2 text-blue-600"
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={deletingId === country._id}
                                    onClick={() => handleDeleteCountry(country._id)}
                                    className="p-2 text-red-600 disabled:opacity-50"
                                  >
                                    <Trash size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}